import { createRoot } from "react-dom/client";
import React from "react";
import { I18nProvider } from "./providers/I18nProvider";
import { WidgetProvider } from "./providers/WidgetProvider";
import { BetSlipProvider } from "./providers/BetSlipProvider";
import { BetBuilderProvider } from "./providers/BetBuilderProvider";
import { ActiveKeyboardProvider } from "./providers/ActiveKeyboardProvider";
import "core-js/stable";
import "regenerator-runtime/runtime";
import BetSlip from "./components/BetSlip";
import { ErrorProvider } from "./providers/ErrorBannerProvider";
import { ConfirmProvider } from "./providers/ConfirmBannerProvider";
import { BonusProvider } from "./providers/BonusProvider";
import "./index.scss";
import "@B2tech-Git/bet-builder-widget/styles.css";
import { StateActionTypes } from "./types/enums";
import { generateUniqueId } from "./shared/utils/helpers";
import {
  dispatchCustomEvent,
  loadBetSlipEvents,
  loadBetSlipFromBookingCode,
  waitForBetBuilderReady,
} from "./shared/utils/bet-slip";

const __ID__ = generateUniqueId();

interface MountOptions {
  config: IWidgetConfig;
  betSlipState: IBetSlipState;
  onStateChange: (state: Partial<IWidgetState>) => void;
  onError: (error: any) => void;
  onNavigate: (route: string) => void;
  onOrderExecuted: (response: ExecuteOrderResponse) => void;
}

export async function mount(container: HTMLElement, options: MountOptions) {
  const root = createRoot(container);

  root.render(
    <WidgetProvider
      id={__ID__}
      config={options.config}
      betSlipState={options.betSlipState}
    >
      <I18nProvider>
        <ErrorProvider>
          <ConfirmProvider>
            <BetSlipProvider
              onStateChange={options.onStateChange}
              onError={options.onError}
              onOrderExecuted={options.onOrderExecuted}
              onNavigate={options.onNavigate}
            >
              <BetBuilderProvider>
                <BonusProvider>
                  <ActiveKeyboardProvider>
                    <BetSlip />
                  </ActiveKeyboardProvider>
                </BonusProvider>
              </BetBuilderProvider>
            </BetSlipProvider>
          </ConfirmProvider>
        </ErrorProvider>
      </I18nProvider>
    </WidgetProvider>,
  );

  await waitForBetBuilderReady();

  return {
    id: __ID__,

    unmount: () => {
      root.unmount();
    },

    addSelection: (payload: IAddSelectionPayload) => {
      dispatchCustomEvent(StateActionTypes.ADD_SELECTION, payload);
    },

    removeSelection: (key: string) => {
      dispatchCustomEvent(StateActionTypes.REMOVE_SELECTION, { key });
    },

    clearAll: () => {
      dispatchCustomEvent(StateActionTypes.CLEAR_ALL_SELECTIONS);
    },
    
    updateConfig: (newConfig: Partial<IWidgetConfig>) => {
      dispatchCustomEvent(StateActionTypes.UPDATE_CONFIG, newConfig);
    },

    loadBookingCode: async (code: string) => {
      await loadBetSlipFromBookingCode({
        code,
        brandId: options.config.brand.id,
      });
    },

    loadBetSlipEvents: async (selections: IBetSlipSelection[]) => {
      await loadBetSlipEvents(selections);
    },

    updateOddsChanges: (data: { data: IEvent[] }) => {
      dispatchCustomEvent(StateActionTypes.UPDATE_ODDS_CHANGES, data);
    },

    removeBetBuilderEvent: (eventId: number) => {
      dispatchCustomEvent(StateActionTypes.REMOVE_BB_EVENT, eventId);
    },

    setAcceptOddsChanges: (value: boolean) => {
      dispatchCustomEvent(StateActionTypes.TOGGLE_ACCEPT_ODDS_CHANGES, value);
    },

    setGlobalStake: (amount: number) => {
      dispatchCustomEvent(StateActionTypes.UPDATE_GLOBAL_STAKE, amount);
    },

    setSource: (source: string | null, sourceRef: string | null) => {
      dispatchCustomEvent(StateActionTypes.SET_SOURCE, { source, sourceRef });
    },
  };
}

const BetSlipWidget = {
  mount,
};

if (typeof window !== "undefined") {
  (window as any).BetSlipWidget = BetSlipWidget;
}

export default BetSlipWidget;
